import { Coordinate, Location, TileSize } from "../../models"
import { BODY_GROUND_OFFSET } from "../../lpc/body_placement"
import type { TileMapMetaProvider } from "../../tile_map_meta_provider"

export type StairDirection = "up" | "down"
export const stairTileAt = (provider: TileMapMetaProvider, at: Coordinate): {
    coordinate: Coordinate; direction: StairDirection; landing?: Location;
} | undefined => {
    const tile = provider.resolveTileMapFromLayerId("sprite2")?.[at.x]?.[at.y]
    if (!tile || tile === "0") return undefined
    const type = provider.resolveTileMeta(tile).entityTileId
    if (type !== "stairs-up" && type !== "stairs-down") return undefined
    const delta = provider.getMapDeltaAt(at.x, at.y, "sprite2")
    if (delta?.removed) return undefined
    return { coordinate: at, direction: type === "stairs-up" ? "up" : "down",
        landing: delta?.context?.context?.stairLanding ?? delta?.context?.stairLanding }
}

/** Respect live support and wall footprints, including feet placement. */
export const stairLandingIsSupported = (map: TileMapMetaProvider, landing: Location): boolean => {
    if (!Number.isFinite(landing.x) || !Number.isFinite(landing.y)) return false
    const x = Math.floor(landing.x / TileSize)
    const stairY = Math.floor(landing.y / TileSize)
    const y = map.isStairAt(x, stairY) ? stairY : Math.floor((landing.y + BODY_GROUND_OFFSET) / TileSize)
    if (x < 0 || y < 0 || x >= map.width() || y >= map.height()) return false
    if (map.isUnsupportedFloorAtPosition({ x: landing.x, y: landing.y + BODY_GROUND_OFFSET })) return false
    if (map.isCollisionAt(x, y) && !map.getTileBlockingProfile(x, y)) return false
    return !map.overlapsTileBlockingProfile(landing.x - 12.5, landing.y - 12.5, landing.x + 12.5, landing.y + 12.5)
}

export const stairLandingNear = (map: TileMapMetaProvider, roomId: string, stair: Coordinate): Location | undefined => {
    for (const [dx, dy] of [[1, 0], [0, 1], [-1, 0], [0, -1]]) {
        const at = { x: stair.x + dx, y: stair.y + dy }
        const landing = { roomId, x: (at.x + 0.5) * TileSize, y: (at.y + 0.5) * TileSize }
        if (!stairTileAt(map, at) && stairLandingIsSupported(map, landing)) return landing
    }
}

/** Arrival and safe standing position are the stair tile itself. */
export const stairExitLanding = (map: TileMapMetaProvider, at: Coordinate): Location | undefined => {
    const landing = { roomId: map.roomId, x: (at.x + .5) * TileSize, y: (at.y + .5) * TileSize }
    return stairTileAt(map, at) && stairLandingIsSupported(map, landing) ? landing : undefined
}
